import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { bannerSlides } from '@/mocks/zh/comics';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % bannerSlides.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = bannerSlides[current];

  return (
    <section className="relative w-full h-[500px] md:h-[650px] overflow-hidden">
      {bannerSlides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={s.cover}
            alt={s.title}
            title={`${s.title} - 韩漫免费网站 - ${s.tag}`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/30 to-foreground-950/60" />

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
        <span className="inline-block bg-accent-500 text-foreground-950 text-xs font-label px-3 py-1 rounded-full mb-6 whitespace-nowrap">
          {slide.tag}
        </span>
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 whitespace-nowrap">
          {slide.title}
        </h1>
        <p className="text-sm md:text-base text-background-100/80 max-w-lg mb-8">
          {slide.subtitle}
        </p>
        <div className="flex items-center gap-4">
          <Link
            to="/category/all"
            className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-sm font-label whitespace-nowrap cursor-pointer transition-all duration-200"
          >
            <i className="ri-play-circle-line mr-2" />
            立即阅读
          </Link>
          <Link
            to={`/category/all`}
            className="border border-background-50/40 hover:border-background-50 text-background-50 px-8 py-3 rounded-full text-sm font-label whitespace-nowrap cursor-pointer transition-all duration-200"
          >
            发现更多
          </Link>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {bannerSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              i === current ? 'bg-primary-500 w-6' : 'bg-background-50/40 hover:bg-background-50/70'
            }`}
            aria-label={`切换到第${i + 1}张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + bannerSlides.length) % bannerSlides.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 cursor-pointer transition-all duration-200"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg" />
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 cursor-pointer transition-all duration-200"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg" />
      </button>
    </section>
  );
}